import { useContext, useState } from 'react';
import { toast } from 'material-react-toastify';
import { TreeItem2 } from '@mui/x-tree-view';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { MenuItem } from '../../../interfaces/Menu/MenuItem';
import { MenuItemTreeControllerContext } from './MenuItemTreeController';
import { deleteMenuItem } from '../../../api/MenuItem';
import { Waiting } from '../../../components/Waiting/Waiting';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';

interface MenuItemTreeNodeProps {
  siblings: MenuItem[];
  index: number;
}

export const MenuItemTreeNode = (props: MenuItemTreeNodeProps) => {
  const menuItem = props.siblings[props.index];

  const { onAddChild, onDelete, onChangeOrder } = useContext(
    MenuItemTreeControllerContext,
  );

  const [clickedDelete, setClickedDelete] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [loading, setLoading] = useState(false);

  const iconStyle = {
    minHeight: 0,
    minWidth: 0,
    p: 0,
    mb: 0.25,
    opacity: hovering ? 1 : 0,
    visibility: hovering ? 'visible' : 'hidden',
    transition: 'opacity 0.3s',
  };

  const handleDelete = () => {
    setLoading(true);
    deleteMenuItem(menuItem.id)
      .then(() => {
        toast.success('Öge başarıyla silindi');
        onDelete?.();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <TreeItem2
      itemId={menuItem.id.toString()}
      label={
        <Box
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Waiting open={loading} />
          <ConfirmDialog
            content={'Gerçekten seçili ögeyi silmek istiyor musunuz?'}
            open={clickedDelete}
            onClose={() => setClickedDelete(false)}
            onConfirm={handleDelete}
            color='error'
          />

          <Typography sx={{ mr: 0.75 }}>{menuItem.name}</Typography>
          <IconButton
            color='primary'
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              onAddChild?.(menuItem.id, menuItem.children.length);
            }}
            sx={iconStyle}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            color='error'
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              setClickedDelete(true);
            }}
            sx={iconStyle}
          >
            <DeleteIcon />
          </IconButton>

          <IconButton
            disabled={props.index === 0}
            color='primary'
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              onChangeOrder?.(props.index, 'up', menuItem.parentID);
            }}
            sx={iconStyle}
          >
            <ArrowUpwardIcon />
          </IconButton>

          <IconButton
            disabled={props.index === props.siblings.length - 1}
            color='primary'
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              onChangeOrder?.(props.index, 'down', menuItem.parentID);
            }}
            sx={iconStyle}
          >
            <ArrowDownwardIcon />
          </IconButton>
        </Box>
      }
    >
      {menuItem.children.map((_, i) => (
        <MenuItemTreeNode siblings={menuItem.children} index={i} />
      ))}
    </TreeItem2>
  );
};
